import type { EmployeeStampProgress, PassportPageSummary } from "@peer-start/platform-contracts";
import { Button, ProgressBar } from "@peer-start/ui";
import { ChevronDown, Eye } from "lucide-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
  competencyScopeLabel,
  competencyStatus,
  competencyToneClasses,
  skillProgressForPage,
  type ProfileSkillRow,
} from "./profile-skills.js";

export function ProfileCompetencyTable({
  pages,
  progress,
  skills,
  expandedIds,
  onToggleExpanded,
  onViewCompetency,
}: {
  pages: PassportPageSummary[];
  progress: EmployeeStampProgress[];
  skills: ProfileSkillRow[];
  expandedIds: Set<string>;
  onToggleExpanded: (pageId: string) => void;
  onViewCompetency: (pageId: string) => void;
}) {
  const { t } = useTranslation();

  if (!pages.length) {
    return <p className="px-5 py-4 text-sm text-muted-foreground">{t("profile.noSkills")}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[32rem] text-left text-sm">
        <thead>
          <tr className="border-b border-border text-muted-foreground">
            <th className="px-5 py-3 font-medium">{t("profile.competencyColumn")}</th>
            <th className="px-5 py-3 font-medium">{t("profile.skillsColumn")}</th>
            <th className="px-5 py-3 font-medium">{t("profile.statusColumn")}</th>
            <th className="px-5 py-3 font-medium">
              <span className="sr-only">{t("common.view")}</span>
            </th>
            <th className="w-10 px-3 py-3" scope="col">
              <span className="sr-only">{t("profile.expandSkills")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {pages.map((summary) => {
            const status = competencyStatus(summary, progress);
            const scope = competencyScopeLabel(summary);
            const skillsProgress = skillProgressForPage(summary.page.id, skills);
            const pageSkills = skills.filter((skill) => skill.pageIds.includes(summary.page.id));
            const expanded = expandedIds.has(summary.page.id);
            const Icon = status.Icon;
            const canExpand = pageSkills.length > 0;

            return (
              <Fragment key={summary.page.id}>
                <tr className="border-b border-border/60">
                  <td className="px-5 py-4">
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <span className="font-medium text-foreground">{summary.page.title}</span>
                      {scope ? (
                        <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
                          {t(scope.key, scope.params)}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-muted-foreground">
                    {skillsProgress.total
                      ? t("profile.skillsProgress", skillsProgress)
                      : t("profile.skillsProgressEmpty")}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${competencyToneClasses[status.tone]}`}
                    >
                      <Icon aria-hidden="true" className="size-3.5 shrink-0" strokeWidth={2} />
                      {t(status.labelKey, status.labelParams)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <Button
                      className="min-h-9 border border-input bg-background px-3 shadow-xs hover:bg-muted"
                      controlId={`profile.skills.view.${summary.page.id}`}
                      onClick={() => onViewCompetency(summary.page.id)}
                      type="button"
                      variant="ghost"
                    >
                      <Eye aria-hidden="true" className="size-4" strokeWidth={1.7} />
                      {t("profile.view")}
                    </Button>
                  </td>
                  <td className="px-3 py-4 text-right">
                    {canExpand ? (
                      <button
                        aria-expanded={expanded}
                        aria-label={t("profile.expandSkills")}
                        className="grid size-8 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                        data-control-id={`profile.skills.expand.${summary.page.id}`}
                        onClick={() => onToggleExpanded(summary.page.id)}
                        type="button"
                      >
                        <ChevronDown
                          aria-hidden="true"
                          className={`size-4 transition-transform ${expanded ? "rotate-180" : ""}`}
                          strokeWidth={2}
                        />
                      </button>
                    ) : null}
                  </td>
                </tr>
                {expanded ? (
                  <tr className="border-b border-border/60 bg-muted/20">
                    <td className="px-5 py-4" colSpan={5}>
                      <ul className="space-y-3">
                        {pageSkills.map((skill) => (
                          <li key={skill.id}>
                            <ProgressBar label={skill.title} layout="inline" value={skill.percent} />
                          </li>
                        ))}
                      </ul>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
